import React, { useState } from 'react';
import {
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Collapse,
  List,
  ListItem,
  IconButton,
  Typography,
  Box,
  Stack,
  Grid,
  Link,
  Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';
import LanguagePicker from '../../common/LanguagePicker';

// I18N
import CustomersFormattedMessages from '../../../messages/admin/CustomersFormattedMessages';

// NETWORKING
import useAdminRequest from '../../../requests/AdminRequest';
import useCustomerRequest from '../../../requests/CustomerRequest';
import { Close, KeyboardArrowRight } from '@mui/icons-material';

const CustomerEditForm = ({
  customer,
  backButtonClick,
  errorHandler,
  tariff_plans,
  class_types,
  sales_agents,
  navigate,
  toggleCampaign,
  toggleNumbersView,
  setToggleCampaign,
  setToggleNumbersView,
  customerStatus,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [campaigns, setCampaigns] = useState(null);
  const [campaignOptions, setCampaignOptions] = useState(null);
  const [campaignType, setCampaignType] = useState('Twilio');
  const [newCampaign, setNewCampaign] = useState('');
  const [edits, setEdits] = useState({});
  const [error, setError] = useState(null);
  const { getCustomerCampaignData } = useCustomerRequest();
  const { updateCustomer } = useAdminRequest();

  console.log(customer);

  const toggleEdit = () => {
    const newEdits = { ...edits };
    delete newEdits.addCampaign;
    delete newEdits.removeCampaign;
    getCampaigns();
    setIsEditing(!isEditing);
    setCampaignType('Twilio');
    setNewCampaign('');
    setEdits(newEdits);
  };

  const handleFormChange = (name, value) => {
    console.log('form change', name, value);
    switch (name) {
      case 'newCampaign':
        if ((!value && value !== '') || value.trim().length > 10) return;
        setNewCampaign(value.trim());
        return;

      case 'campaignType':
        setCampaignType(value);
        return;

      case 'code_type':
        setEdits({
          ...edits,
          customer_type: value.charAt(0),
          customer_class: value.charAt(1),
        });
        return;

      case 'formCountry':
        setEdits({
          ...edits,
          country_id: value,
        });
        return;

      default:
        setEdits({
          ...edits,
          [name]: value,
        });
        return;
    }
  };

  const validatePhoneNumber = async (number) => {
    const regExPattern = /^\+?\d+$/;
    return regExPattern.test(number);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const def_orig = edits.default_originator;
      const valid = await validatePhoneNumber(def_orig);
      if (
        def_orig &&
        (def_orig.length > 15 ||
          ((def_orig.charAt(0) !== '+' || !valid) && def_orig.length > 11))
      ) {
        setError(
          "The Originator should not be longer than 11 characters (or 15 characters if its a phone number starting with a '+')"
        );
        setIsSaving(false);
        return;
      }

      setIsSaving(true);
      await updateCustomer(customer.customer_id, edits, errorHandler);
      backButtonClick('refresh');
    } catch (e) {
      setError(e.message);
      setIsSaving(false);
    }
  };

  const generateError = () => {
    if (!error) return null;
    return <Alert severity="error">{error}</Alert>;
  };

  const generateFormButton = () => {
    if (isSaving) return <Typography>Saving...</Typography>;
    if (customerStatus === 'C')
      return <Typography>Customer is not active.</Typography>;

    if (!isEditing)
      return (
        <Button variant="contained" color="secondary" onClick={toggleEdit}>
          {CustomersFormattedMessages.edit}
        </Button>
      );

    return (
      <Box display="flex" gap={2}>
        <Button variant="outlined" color="secondary" onClick={toggleEdit}>
          {CustomersFormattedMessages.cancel}
        </Button>

        <Button variant="contained" color="secondary" type="submit">
          {CustomersFormattedMessages.save}
        </Button>
      </Box>
    );
  };

  const generateClassTypeSelect = () => {
    const classTypeLists = class_types.map((classType) => (
      <MenuItem
        key={classType.class_type_status_id}
        value={classType.code_type + classType.code_class}
      >
        {classType.select_name}
      </MenuItem>
    ));

    const classTypeValue = edits.customer_class
      ? `${edits.customer_type}${edits.customer_class}`
      : `${customer.customer_type}${customer.customer_class}`;
    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }} id="code-type-label">
          Type - Class
        </InputLabel>
        <Select
          labelId="code-type-label"
          id="code-type"
          name="code_type"
          value={classTypeValue}
          onChange={(e) => handleFormChange('code_type', e.target.value)}
          size="small"
          fullWidth
        >
          {classTypeLists}
        </Select>
      </Stack>
    );
  };

  const generateTariffPlanSelect = () => {
    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }} id="tariff-plan-label">
          Tariff Plan
        </InputLabel>
        <Select
          labelId="tariff-plan-label"
          id="tariff-plan"
          name="tariff_plan_id"
          value={customer ? customer.tariff_plan_id : null}
          size="small"
          fullWidth
          onChange={(e) => handleFormChange('tariff_plan_id', e.target.value)}
        >
          {tariff_plans.map((tariffPlan) => (
            <MenuItem
              key={tariffPlan.tariff_plan_id}
              value={tariffPlan.tariff_plan_id}
            >
              {tariffPlan.select_name}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const generateSalesAgentSelect = () => {
    let salesAgentList = (
      <MenuItem hidden key={''} value={''}>
        {'Loading...'}
      </MenuItem>
    );

    if (sales_agents) {
      salesAgentList = [
        <MenuItem key={''} value={''}>
          {'None'}
        </MenuItem>,
      ];

      sales_agents.map((salesAgent) =>
        salesAgentList.push(
          <MenuItem
            key={salesAgent.sales_agent_id}
            value={salesAgent.sales_agent_id}
          >
            {salesAgent.sales_agent_name}
          </MenuItem>
        )
      );
    }
    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }} id="sales-agent-label">
          Sales Agent
        </InputLabel>
        <Select
          labelId="sales-agent-label"
          id="sales-agent"
          name="sales_agents_id"
          value={edits?.sales_agents_id || customer?.sales_agents_id || ''}
          size="small"
          fullWidth
          displayEmpty
          onChange={(e) => handleFormChange('sales_agents_id', e.target.value)}
        >
          {salesAgentList}
        </Select>
      </Stack>
    );
  };

  const removeCampaign = (i, code) => {
    let newEdits = { ...edits };

    // If a campaign was added, check if the campaign to be removed was the new campaign in which case,
    // don't add the new campaign.
    if (newEdits.addCampaign) {
      const search = newEdits.addCampaign.findIndex(
        (element) => element.campaign_code === code
      );
      if (search !== -1) {
        newEdits.addCampaign.splice(search, 1);
        setEdits(newEdits);
        return;
      }
    }

    newEdits.removeCampaign ??= [];
    newEdits.removeCampaign.push(code);

    setEdits(newEdits);
  };

  const addCampaign = () => {
    if (newCampaign === '') return;

    // If the campaign code is already in the addCampaign array or in the campaigns array, do nothing.
    if (
      (edits.addCampaign &&
        edits.addCampaign.findIndex(
          (element) => element.campaign_code === newCampaign
        ) !== -1) ||
      (campaigns &&
        campaigns.findIndex(
          (element) => element.campaign_code === newCampaign
        ) !== -1)
    )
      return;

    const newCampaignObj = {
      campaign_code: newCampaign,
      campaign_type: campaignType,
    };

    // If a campaign is also being removed, check if the removed campaign is the same as the new campaign in which case
    // don't remove the campaign.
    if (edits.removeCampaign) {
      const search = edits.removeCampaign.indexOf(newCampaign);
      if (search !== -1) {
        edits.removeCampaign.splice(search, 1);
        setNewCampaign('');
        setEdits(edits);
        return;
      }
    }

    edits.addCampaign ??= [];
    edits.addCampaign.push(newCampaignObj);

    setNewCampaign('');
    setEdits(edits);
  };

  const generateCampaignEdit = () => {
    let items = [];

    const campaignOptionsArray =
      campaignOptions?.map(({ campaign_code }) => ({
        label: campaign_code,
        value: campaign_code,
      })) || [];

    let campaignLabel = 'Loading...';
    if (campaignOptions && campaignOptions.length > 0) {
      campaignLabel = 'Select Campaign Code';
    } else if (campaignOptions) {
      campaignLabel = 'No other campaigns found for this brand.';
    }

    if (!campaigns) items = CustomersFormattedMessages.loading;
    else if (campaigns.length === 0 && edits.addCampaign?.length === 0)
      items = CustomersFormattedMessages.noCampaignFound;
    else {
      const list = edits.addCampaign
        ? campaigns.concat(edits.addCampaign)
        : campaigns;
      list.map((campaign, i) => {
        if (
          (edits.removeCampaign &&
            !edits.removeCampaign.includes(campaign.campaign_code)) ||
          !edits.removeCampaign
        ) {
          items.push(
            <ListItem key={i}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flex: 1,
                  paddingRight: 3,
                }}
              >
                {campaign.campaign_code}
                <Typography component="em" alignSelf="end">
                  {campaign.campaign_type}
                </Typography>
              </Box>
              <IconButton
                color="error"
                onClick={() => removeCampaign(i, campaign.campaign_code)}
              >
                <Close />
              </IconButton>
            </ListItem>
          );
        }
      });
    }

    return (
      <FormGroup sx={{ width: '100%' }}>
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            alignItems: { md: 'center' },
            gap: { md: 2 },
            marginBottom: 2,
          }}
        >
          <InputLabel sx={{ minWidth: 200 }}>Campaigns</InputLabel>
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <FormControl>
              <Select
                value={campaignType}
                name="campaignType"
                onChange={(e) =>
                  handleFormChange('campaignType', e.target.value)
                }
                size="small"
              >
                <MenuItem key={'twilio'} value={'Twilio'}>
                  Twilio
                </MenuItem>
                <MenuItem key={'bandwidth'} value={'Bandwidth'}>
                  Bandwidth
                </MenuItem>
                <MenuItem key={'telnyx'} value={'Telnyx'}>
                  Telnyx
                </MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              disablePortal
              onChange={(_, newValue) => {
                handleFormChange('newCampaign', newValue?.value || '');
              }}
              size="small"
              variant="outlined"
              options={campaignOptionsArray}
              disabled={!campaignOptions?.length}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label={campaignLabel} />
              )}
            />
            <IconButton onClick={addCampaign} size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </Stack>
        {items.length > 0 && (
          <List
            disablePadding
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.400',

              '& .MuiListItem-root:not(:last-of-type)': {
                borderBottom: '1px solid',
                borderColor: 'grey.300',
              },
              marginLeft: { md: 27 },
            }}
          >
            {items}
          </List>
        )}
      </FormGroup>
    );
  };

  const toggleChevronClick = async (field, fetchCallback) => {
    if (field === 'toggleNumbersView') setToggleNumbersView(!toggleNumbersView);
    else if (field === 'toggleCampaign') setToggleCampaign(!toggleCampaign);
    fetchCallback();
  };

  const getCampaigns = async () => {
    if (!campaigns && customer) {
      const results = await getCustomerCampaignData(
        customer.customer_id,
        errorHandler
      );
      console.log('Campaign results:', results);
      setCampaigns(results.currentCampaigns);
      setCampaignOptions(results.brandCampaignOptions);
    }
  };

  const generateFormGroup = (label, displayValue, controlId, onClick) => {
    if (label === 'Campaigns:') {
      let items = [];

      if (!campaigns)
        items = <Typography>{CustomersFormattedMessages.loading}</Typography>;
      else if (campaigns.length === 0)
        items = (
          <Typography>{CustomersFormattedMessages.noCampaignFound}</Typography>
        );
      else {
        items = (
          <List
            disablePadding
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.400',
              width: '100%',
              marginTop: 2,
              maxWidth: 800,
              '& .MuiListItem-root:not(:last-of-type)': {
                borderBottom: '1px solid',
                borderColor: 'grey.300',
              },
            }}
          >
            {campaigns.map((campaign, i) => (
              <ListItem key={i}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flex: 1,
                  }}
                >
                  {campaign.campaign_code}
                  <Typography component="em" alignSelf="end">
                    {campaign.campaign_type}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        );
      }

      return (
        <Grid size={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={700}>{label}</Typography>
            <Button
              startIcon={
                toggleCampaign ? <ExpandMoreIcon /> : <KeyboardArrowRight />
              }
              variant="link"
              color="secondary"
              sx={{ paddingY: 0 }}
              onClick={() => toggleChevronClick('toggleCampaign', getCampaigns)}
            >
              Show All Campaigns
            </Button>
          </Box>
          <Collapse in={toggleCampaign}>{items}</Collapse>
        </Grid>
      );
    }

    return (
      <Grid size={12}>
        <Typography>
          <strong>{label}</strong>{' '}
          {onClick ? (
            <Link tabIndex={0} onClick={onClick}>
              {displayValue}
            </Link>
          ) : (
            displayValue
          )}
        </Typography>
      </Grid>
    );
  };

  const generateBody = () => {
    if (customer && !isEditing) {
      return (
        <Grid container spacing={2}>
          {generateFormGroup(
            "Customer's Name:",
            customer.customer_name,
            'customer_name'
          )}
          {generateFormGroup(
            'Account Id:',
            customer.customer_account_no || '',
            'customer_account_no'
          )}
          {generateFormGroup(
            'Customer No (ABN):',
            customer.customer_abn || '',
            'customer_abn'
          )}
          {generateFormGroup(
            'Customer Reference No:',
            customer.customer_ref_no || '',
            'customer_ref_no'
          )}
          {generateFormGroup(
            'Default Originator:',
            customer.default_originator || '',
            'default_originator'
          )}
          {generateFormGroup(
            'Type - Class:',
            `${customer.customer_type_name} - ${customer.customer_class_name}`,
            'type_class'
          )}
          {generateFormGroup(
            'Status:',
            customer.customer_status_name,
            'customer_status'
          )}
          {generateFormGroup(
            'Sales Agent:',
            customer.sales_agent_name || '',
            'sales_agent_id'
          )}
          {generateFormGroup(
            'Tariff Plan:',
            customer.tariff_plan_name || '',
            'tariff_plan_id',
            () => {
              navigate('/admin/tariffs', {
                state: {
                  tariffPlanId: customer.tariff_plan_id,
                  tariffSchemeId: customer.tariff_scheme_id,
                },
              });
            }
          )}
          {generateFormGroup(
            'Country:',
            customer.country_name || 'Unknown',
            'country_name'
          )}
          {generateFormGroup(
            'Language:',
            customer.language_name || 'English',
            'language_id'
          )}
          {generateFormGroup(
            'Brand:',
            customer.brand_code || 'N/A',
            'brand_code',
            customer.brand_code // This is only a link if brand_code exists.
              ? () => navigate('/admin/compliance/brand/' + customer.brand_id)
              : undefined
          )}
          {generateFormGroup(
            'Campaigns:',
            customer.campaigns || [],
            'campaigns'
          )}
          {generateFormGroup(
            'Contract Form Id:',
            customer.contract_id || '',
            'contract_id'
          )}
          {generateFormGroup(
            'Prepaid Credits:',
            customer.sms_in_excess,
            'sms_in_excess'
          )}
          {generateFormGroup(
            'HTTP Reply Template:',
            customer.reply_template || 'None',
            'reply_template'
          )}
          {generateFormGroup(
            'HTTP Reply URL:',
            customer.reply_url || '',
            'reply_url'
          )}
          {generateFormGroup(
            'Last Billed:',
            customer.last_billed || '',
            'last_billed'
          )}
          {generateFormButton()}
        </Grid>
      );
    } else if (customer && isEditing) {
      return (
        <Box
          component="form"
          onSubmit={handleSubmit}
          onChange={(e) => handleFormChange(e.target.name, e.target.value)}
          maxWidth={800}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  Customer&rsquo;s Name
                </InputLabel>
                <TextField
                  name="customer_name"
                  type="text"
                  defaultValue={customer.customer_name}
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                />
              </Stack>
            </Grid>
            {generateFormGroup(
              'Account Id:',
              customer.customer_account_no || '',
              'customer_account_no'
            )}
            {generateFormGroup(
              'Customer No (ABN):',
              customer.customer_abn || '',
              'customer_abn'
            )}
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.customerReferenceNo}
                </InputLabel>
                <TextField
                  name="customer_ref_no"
                  type="text"
                  defaultValue={customer.customer_ref_no}
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.defaultOriginator}
                </InputLabel>
                <TextField
                  type="text"
                  defaultValue={customer.default_originator}
                  name="default_originator"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>
              <Typography
                color="error"
                fontSize="small"
                sx={{ marginLeft: { xs: 0, md: 28 } }}
              >
                {CustomersFormattedMessages.dangerText}
              </Typography>
            </Grid>
            <Grid size={12}>{generateClassTypeSelect()}</Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} id="customer-status-label">
                  {CustomersFormattedMessages.status}
                </InputLabel>
                <Select
                  labelId="customer-status-label"
                  id="customer-status"
                  name="customer_status"
                  value={edits.customer_status || customer.customer_status}
                  size="small"
                  fullWidth
                  onChange={(e) =>
                    handleFormChange('customer_status', e.target.value)
                  }
                >
                  <MenuItem value="A">
                    {CustomersFormattedMessages.active}
                  </MenuItem>
                  <MenuItem value="C">
                    {CustomersFormattedMessages.cancelled}
                  </MenuItem>
                  <MenuItem value="P">
                    {CustomersFormattedMessages.pendingCancelled}
                  </MenuItem>
                  <MenuItem value="S">
                    {CustomersFormattedMessages.suspended}
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>{generateSalesAgentSelect()}</Grid>
            <Grid size={12}>{generateTariffPlanSelect()}</Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.country}
                </InputLabel>

                <CountrySelect
                  name="country_id"
                  value={edits.country_id || customer.country_id}
                  onChange={(_, newValue) =>
                    handleFormChange('country_id', newValue)
                  }
                  required
                  fullWidth
                  selectDefault
                  errorHandler={errorHandler}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                {generateCampaignEdit()}
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.contractFormId}
                </InputLabel>
                <TextField
                  type="text"
                  defaultValue={customer.contract_id}
                  name="contract_id"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} id="form-lang-label">
                  {CustomersFormattedMessages.language}
                </InputLabel>
                <LanguagePicker
                  name="language_id"
                  value={edits.language_id || customer.language_id}
                  size="small"
                  displayEmpty
                  fullWidth
                  onChange={(newValue) =>
                    handleFormChange('language_id', newValue)
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.active_creation === 'Y'}
                      name="active_creation"
                    />
                  }
                  label={CustomersFormattedMessages.activeCreation}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.red_alert_domain_flag === 'Y'}
                      name="red_alert_domain_flag"
                    />
                  }
                  label={CustomersFormattedMessages.redAlertEnabled}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.sales_tax_flag === 'Y'}
                      name="sales_tax_flag"
                    />
                  }
                  label={CustomersFormattedMessages.gSTApplicable}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.oms_flag === 'Y'}
                      name="oms_flag"
                    />
                  }
                  label={CustomersFormattedMessages.oMSFlag}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.receipt_flag === 'Y'}
                      name="receipt_flag"
                    />
                  }
                  label={CustomersFormattedMessages.receiptFlag}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked={customer.employee_trust_flag === 'Y'}
                      name="employee_trust_flag"
                    />
                  }
                  label={CustomersFormattedMessages.employeeTrustFlag}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} id="message-priority-label">
                  {CustomersFormattedMessages.messagePriority}
                </InputLabel>
                <Select
                  labelId="message-priority-label"
                  id="message-priority"
                  name="message_priority"
                  defaultValue={customer.message_priority}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="0">
                    {CustomersFormattedMessages.low.props.defaultMessage.toString()}
                  </MenuItem>
                  <MenuItem value="1">
                    {CustomersFormattedMessages.medium.props.defaultMessage.toString()}
                  </MenuItem>
                  <MenuItem value="2">
                    {CustomersFormattedMessages.high.props.defaultMessage.toString()}
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.prepaidCredits}
                </InputLabel>
                <TextField
                  type="text"
                  defaultValue={customer.sms_in_excess}
                  name="sms_in_excess"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }} id="reply-template-label">
                  {CustomersFormattedMessages.hTTPReplyTemplate}
                </InputLabel>
                <Select
                  labelId="reply-template-label"
                  id="reply-template"
                  name="reply_template"
                  defaultValue={customer?.reply_template ?? ''}
                  size="small"
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="">
                    {CustomersFormattedMessages.none}
                  </MenuItem>
                  <MenuItem value="RO4HttpReply.txt">
                    {CustomersFormattedMessages.rO4HttpReplyTxt}
                  </MenuItem>
                  <MenuItem value="RO4HttpReplyFormData.txt">
                    {CustomersFormattedMessages.rO4HttpReplyFormDataTxt}
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 200 }}>
                  {CustomersFormattedMessages.hTTPReplyURL}
                </InputLabel>
                <TextField
                  type="text"
                  defaultValue={customer.reply_url}
                  name="reply_url"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              {generateError()}
              {generateFormButton()}
            </Grid>
          </Grid>
        </Box>
      );
    }

    return <Typography> {CustomersFormattedMessages.noData}</Typography>;
  };

  return <>{generateBody()}</>;
};

export default CustomerEditForm;
